<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { UserButton } from '@clerk/vue'
import Sidebar from '@/components/Sidebar.vue'
import OrgSwitcher from '@/components/OrgSwitcher.vue'
import ActivityBell from '@/components/ActivityBell.vue'
import ToastContainer from '@/components/ToastContainer.vue'
import OwnershipPrompt from '@/components/OwnershipPrompt.vue'
import { primeChime } from '@/lib/chime'
import { isDevAuth, useAuthState } from '@/lib/auth'
import { markBoot } from '@/lib/bootprofile'
import { useSessionStore } from '@/stores/session'
import { useEventsStore } from '@/stores/events'
import { useNotificationsStore } from '@/stores/notifications'
import { useFeaturesStore } from '@/stores/features'
import { usePursuitsStore } from '@/stores/pursuits'
import { useOwnershipStore } from '@/stores/ownership'

const router = useRouter()
const route = useRoute()

// Mobile nav drawer — the fixed sidebar is desktop-only (md+).
const mobileNavOpen = ref(false)

watch(() => route.fullPath, () => {
  mobileNavOpen.value = false
})
const { isLoaded, isSignedIn } = useAuthState()
const session = useSessionStore()
// Surface slow-auth so a long wait is diagnosable instead of a mystery.
const authSlow = ref(false)
let slowTimer: ReturnType<typeof setTimeout> | null = null
onMounted(() => {
  slowTimer = setTimeout(() => {
    authSlow.value = true
  }, 4000)
})
watch(isLoaded, (loaded) => {
  if (loaded) {
    if (slowTimer) clearTimeout(slowTimer)
    markBoot('authLoaded')
  }
}, { immediate: true })

const events = useEventsStore()
const notifications = useNotificationsStore()
const featureFlags = useFeaturesStore()
const pursuits = usePursuitsStore()
const ownership = useOwnershipStore()

// Redirect to sign-in when not authenticated (no-op in dev auth mode).
watch([isLoaded, isSignedIn], ([loaded, signedIn]) => {
  if (loaded && !signedIn) {
    router.replace('/sign-in')
  }
}, { immediate: true })

// Load /me (memberships, role) for the org switcher and role gating.
onMounted(() => {
  session.init()
})

// SSE + notifications follow the active org: teardown/reset on switch,
// (re)connect for the new org.
watch(
  () => session.activeOrgId,
  (orgId, prevOrgId) => {
    if (prevOrgId && orgId !== prevOrgId) {
      events.stop()
      notifications.reset()
      pursuits.reset()
      ownership.reset()
    }
    if (orgId) {
      events.start(orgId)
      notifications.fetch()
      ownership.fetch()
      featureFlags.reset()
      featureFlags.load(orgId)
    }
  },
  { immediate: true },
)

/**
 * A hand-off request grants itself in ten minutes, so the prompt cannot depend
 * on the SSE stream alone — a dropped connection would mean losing a pursuit
 * without ever seeing the ask. A minute's poll bounds that: worst case you get
 * nine minutes of the window instead of ten.
 */
let ownershipPoll: ReturnType<typeof setInterval> | undefined
onMounted(() => {
  ownershipPoll = setInterval(() => {
    if (session.activeOrgId && document.visibilityState === 'visible') ownership.fetch(true)
  }, 60_000)
  // Browsers won't let a page make noise until it has been interacted with;
  // the first click of the session is what buys the right to chime later.
  document.addEventListener('pointerdown', primeChime, { once: true })
})

onUnmounted(() => {
  events.stop()
  clearInterval(ownershipPoll)
})
</script>

<template>
  <!-- Loading state while Clerk initializes -->
  <div v-if="!isLoaded" class="min-h-screen bg-surface flex flex-col items-center justify-center gap-2">
    <span class="text-label-caps uppercase text-ink-muted">Loading...</span>
    <span v-if="authSlow" class="text-body-sm text-ink-muted">
      Still connecting to the sign-in service — slow network?
    </span>
  </div>

  <!-- Not signed in: show nothing, redirect is in flight -->
  <div v-else-if="!isSignedIn" class="min-h-screen bg-surface flex items-center justify-center">
    <span class="text-label-caps uppercase text-ink-muted">Redirecting to sign in...</span>
  </div>

  <!-- Signed in: top bar + left sidebar + content -->
  <div v-else class="min-h-screen bg-surface">
    <!-- Top bar -->
    <header class="fixed top-0 left-0 right-0 z-40 h-14 bg-navy text-white flex items-center gap-2 sm:gap-4 px-3 sm:px-4">
      <button
        class="md:hidden -ml-1 p-2 rounded hover:bg-white/10"
        aria-label="Open navigation"
        @click="mobileNavOpen = true"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>
      <RouterLink to="/" class="flex items-baseline select-none">
        <span class="text-body-lg font-bold tracking-tight text-white">GovCRM</span>
        <span class="text-body-lg font-bold text-accent leading-none">.</span>
      </RouterLink>

      <div class="flex-1" />

      <!-- Admin role preview: UI-only; the server enforces the real role. -->
      <div
        v-if="session.viewAsRole"
        class="hidden sm:flex items-center gap-2 px-3 py-1 rounded-full bg-status-amber-soft text-status-amber text-label-caps uppercase"
      >
        Viewing as {{ session.viewAsRole === 'proposal_writer' ? 'Proposal Writer' : 'Procurement Specialist' }}
        <button class="underline hover:no-underline" @click="session.setViewAsRole(null)">Exit</button>
      </div>

      <ActivityBell />

      <OrgSwitcher />

      <UserButton v-if="!isDevAuth" />
      <span
        v-else
        class="text-label-caps uppercase px-2 py-1 rounded bg-status-amber-soft text-status-amber"
        title="VITE_AUTH_MODE=dev — Clerk bypassed"
      >
        dev
      </span>
    </header>

    <!-- Sidebar (desktop) -->
    <div class="hidden md:block fixed left-0 top-14 bottom-0 w-[220px] z-30 bg-surface-card border-r border-subtle">
      <Sidebar />
    </div>

    <!-- Sidebar (mobile drawer) -->
    <Teleport to="body">
      <div v-if="mobileNavOpen" class="md:hidden fixed inset-0 z-50">
        <div class="absolute inset-0 bg-navy/50" @click="mobileNavOpen = false" />
        <div class="absolute left-0 top-0 bottom-0 w-[250px] bg-surface-card border-r border-subtle shadow-xl flex flex-col">
          <div class="h-14 flex items-center justify-between px-4 bg-navy text-white">
            <span class="text-body-lg font-bold tracking-tight">GovCRM<span class="text-accent">.</span></span>
            <button class="p-2 -mr-2 rounded hover:bg-white/10" aria-label="Close navigation" @click="mobileNavOpen = false">✕</button>
          </div>
          <div class="flex-1 overflow-y-auto">
            <Sidebar />
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Main content -->
    <main class="pt-14 md:pl-[220px] min-h-screen">
      <div class="p-3 sm:p-4 md:p-6 lg:p-8">
        <slot />
      </div>
    </main>

    <OwnershipPrompt />
    <ToastContainer />
  </div>
</template>
